import { MessageResponseDto } from "@api/api";
import { CARDOWNER_ROLE_ID } from "@core/model/const";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { personFormDefault } from "@factory/form/form.default";
import { PersonFormRule, PersonFormSchema } from "@factory/form/form.rule";
import { GetPersonModel } from "@factory/model/query-model-person";
import { useQyAutoAssignCardSeries } from "@factory/query/card-series.query";
import { useQyCreatePerson } from "@factory/query/person.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

export function useNewCustomer() {
  // LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const { tenant } = getUserRole();
  const navigate = useNavigate();
  const { mapCreatePerson, mapAutoAssignCardSeries } = useFormApiMapper();
  const { showError, showSuccess, showWarning } = useNotificationContext();

  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    showSuccess(`New Customer is created`);
    navigate(`../${data.code}`);
  };
  const { mutateAsync: addPerson, isLoading: isCreating } =
    useQyCreatePerson(handleApiSuccess);

  const { mutateAsync: assignCard } = useQyAutoAssignCardSeries();

  // LOCAL FUNCTIONS
  const formMethod = useForm<PersonFormSchema>({
    defaultValues: personFormDefault,
    resolver: zodResolver(PersonFormRule),
  });
  const { handleSubmit } = formMethod;

  const handleValidate = async (
    form: PersonFormSchema,
    isAutoCardAssign: boolean
  ) => {
    if (isAutoCardAssign && !form.cardCategory) {
      showWarning("Card category is required");
      return;
    }

    const updatedForm = {
      ...form,
      role: CARDOWNER_ROLE_ID,
      tenant,
    };
    const formData = mapCreatePerson(updatedForm);
    const personResponse = await addPerson(formData);
    const personData = personResponse.data as GetPersonModel;

    if (!isAutoCardAssign) {
      navigate(`../${personData.code}`);
      return;
    }

    const cardFormData = mapAutoAssignCardSeries(
      personData.code,
      form.cardCategory ?? "",
      tenant
    );
    await assignCard(cardFormData);

    navigate(`../${personData.code}`);
  };
  const handleValidateError = (err: FieldErrors<PersonFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return {
    isCreating,
    formMethod,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
  };
}
